// uniforms.ts — a tiny, explicit uniform-buffer helper. No reflection, no magic.
// We lay out the bytes by hand so the WGSL struct and the JS struct can never
// silently drift: change one, change the other, on purpose.

import type { GPU } from "./gpu";

/**
 * The per-frame globals every material can read at @group(0) @binding(0).
 * WGSL std140-ish layout (16-byte aligned, vec2 on 8-byte boundaries):
 *
 *   struct Globals {
 *     resolution : vec2<f32>,  // px, offset  0
 *     time       : f32,        //     offset  8
 *     scroll     : f32,        //     offset 12  (0..1 reading progress)
 *     pointer    : vec2<f32>,  // ndc, offset 16
 *     warp       : f32,        //     offset 24  (0..1 reader-open warp)
 *     _pad       : f32,        //     offset 28
 *   };                         // size 32
 */
export class Globals {
  static readonly BYTES = 32;
  static readonly WGSL = /* wgsl */ `
struct Globals {
  resolution : vec2<f32>,
  time       : f32,
  scroll     : f32,
  pointer    : vec2<f32>,
  warp       : f32,
  _pad       : f32,
};`;

  readonly buffer: GPUBuffer;
  private readonly device: GPUDevice;
  private readonly data = new Float32Array(Globals.BYTES / 4);

  constructor(gpu: GPU) {
    this.device = gpu.device;
    this.buffer = gpu.device.createBuffer({
      label: "globals",
      size: Globals.BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  set resolution(v: readonly [number, number]) {
    this.data[0] = v[0];
    this.data[1] = v[1];
  }
  set time(v: number) {
    this.data[2] = v;
  }
  set scroll(v: number) {
    this.data[3] = v;
  }
  set pointer(v: readonly [number, number]) {
    this.data[4] = v[0];
    this.data[5] = v[1];
  }
  set warp(v: number) {
    this.data[6] = v;
  }

  /** Push the current values to the GPU. Call once per frame. */
  upload(): void {
    this.device.queue.writeBuffer(this.buffer, 0, this.data);
  }
}
